/* eslint-disable react/jsx-pascal-case */
/* Original name @uswitch/trustyle.card */

import * as React from 'react'
import { Themed } from 'theme-ui'
import ImgixImg from '../imgix-img'
import { ButtonLink } from '../button-link'
import dayjs from '@uswitch/ev-utils/lib/trustyle-date'
import { DynamicSparkIcon } from '../spark-icons/dynamic-spark-icon'

const makeStyles = variant => element =>
  `compounds.card.variants.${variant}${element ? `.${element}` : ''}`

const Card = ({
  className = '',
  critical = false,
  date,
  description,
  imageSize = 'cover',
  imgAlt,
  imgSizes = '768px',
  imgSrc,
  imageProps = {},
  linkHref,
  linkIcon,
  linkText,
  superScript,
  tag,
  title,
  variant = 'vertical',
  headerChildren,
  contentChildren,
  onClick = () => {},
  trackInteraction = () => {},
  button,
}) => {
  const styles = makeStyles(variant)
  const isAspectRatioBox = variant === 'vertical' || variant === 'horizontal'

  const HeaderWrapper = children => (
    <React.Fragment>
      <If condition={headerChildren}>
        <div sx={{ variant: styles('headerChildren') }}>
          <div sx={{ variant: styles('headerChildren') }}>{headerChildren}</div>
          <span>{children}</span>
        </div>
      </If>

      <If condition={!headerChildren}>{children}</If>
    </React.Fragment>
  )

  const AspectRatioWrapper = children => (
    <React.Fragment>
      <If condition={isAspectRatioBox}>
        <div sx={{ variant: styles('aspectRatioWrapper') }}>
          <Themed.a sx={{ variant: styles('image') }} href={linkHref} onClick={trackInteraction}>
            <div sx={{ variant: styles('aspectRatioBox') }}>
              <div sx={{ variant: styles('cardImage'), width: '100%' }}>{children}</div>
            </div>
          </Themed.a>
        </div>
      </If>

      <If condition={!isAspectRatioBox}>
        <Themed.a sx={{ variant: styles('image') }} href={linkHref} onClick={trackInteraction}>
          {children}
        </Themed.a>
      </If>
    </React.Fragment>
  )

  const journeyVariant = variant === 'journey-card'

  const JourneyButton = (
    <ButtonLink
      variant='primary-journey'
      href={linkHref}
      afterIcon={linkIcon}
      onClick={e => {
        trackInteraction(e)
        onClick(e)
      }}
    >
      {linkText}
    </ButtonLink>
  )

  return (
    <div className={className} sx={{ variant: styles() }}>
      {HeaderWrapper(
        AspectRatioWrapper(
          <ImgixImg
            alt={imgAlt}
            src={imgSrc}
            sizes={imgSizes}
            critical={critical}
            ctfToUswitchContenftul={false}
            clearPx={false}
            imgixAutoFormatCompress={false}
            svgAsImg={false}
            {...imageProps}
            imgixParams={{
              fit: imageSize === 'cover' ? 'crop' : 'fill',
              crop: 'faces,entropy',
              ar: '16:9',
              fill: 'solid',
              ...(imageProps.imgixParams || {}),
            }}
          />,
        ),
      )}

      <div
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1',
          variant: styles('content'),
        }}
      >
        <div
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            variant: styles('meta'),
          }}
        >
          {date && (
            <Themed.p as='div' sx={{ variant: styles('date') }}>
              {dayjs(date).format('DD MMMM YYYY')}
            </Themed.p>
          )}

          {tag && <span sx={{ marginRight: 'sm', variant: styles('tag') }}>{tag}</span>}

          {superScript && (
            <Themed.p as='div' sx={{ variant: styles('superScript') }}>
              <p>{superScript}</p>
            </Themed.p>
          )}

          {contentChildren && (
            <div sx={{ variant: styles('contentChildren') }}>{contentChildren}</div>
          )}
        </div>
        {title && (
          <Themed.h3 sx={{ margin: '0', variant: styles('heading') }}>
            <Themed.a href={linkHref} onClick={trackInteraction}>
              {title}
            </Themed.a>
          </Themed.h3>
        )}
        {description && <Themed.p sx={{ variant: styles('description') }}>{description}</Themed.p>}
        {linkText && !journeyVariant && (
          <Themed.a
            href={linkHref}
            onClick={trackInteraction}
            sx={{ textDecoration: 'underline', variant: styles('link') }}
          >
            {linkIcon && <DynamicSparkIcon glyph={linkIcon} color='brand' />}
            {linkText}
          </Themed.a>
        )}
        {linkText && journeyVariant && ((button && button) || JourneyButton)}
      </div>
    </div>
  )
}

export default Card
